import { apiClient } from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import type {
  FitLauncherDeleteResponse,
  FitLauncherDeploymentCreateRequest,
  FitLauncherDeploymentCreateResponse,
  FitLauncherDeploymentResponse,
  FitLauncherDownloadUrlResponse,
  FitLauncherPlanRequest,
  FitLauncherPlanResponse,
  FitLauncherReadmeResponse,
  RevokeApiKeyResponse,
  RotateApiKeyResponse,
} from '@/api/types';

/** Fit launcher deployments: self-hosted launches of a Fit result's models.
 *  Every route is scoped to a project, except the user-wide list. */
export const fitLauncherService = {
  /** Creates a new deployment (one per host). The response is the only place
   *  the ingestion token ever appears. */
  create: (projectId: string, body: FitLauncherDeploymentCreateRequest) =>
    apiClient
      .post<FitLauncherDeploymentCreateResponse>(API_ENDPOINTS.FIT_LAUNCHER.LIST(projectId), body)
      .then((r) => r.data),

  /** Whether the create would accept a selection, with each GPU's usage and
   *  the models that still fit alongside it. Creates nothing. */
  plan: (body: FitLauncherPlanRequest) =>
    apiClient
      .post<FitLauncherPlanResponse>(API_ENDPOINTS.FIT_LAUNCHER.PLAN, body)
      .then((r) => r.data),

  /** Every launcher deployment of the current user, across projects. */
  listMine: () =>
    apiClient
      .get<FitLauncherDeploymentResponse[]>(API_ENDPOINTS.FIT_LAUNCHER.MINE)
      .then((r) => r.data),

  list: (projectId: string) =>
    apiClient
      .get<FitLauncherDeploymentResponse[]>(API_ENDPOINTS.FIT_LAUNCHER.LIST(projectId))
      .then((r) => r.data),

  detail: (projectId: string, deploymentId: string) =>
    apiClient
      .get<FitLauncherDeploymentResponse>(API_ENDPOINTS.FIT_LAUNCHER.DETAIL(projectId, deploymentId))
      .then((r) => r.data),

  /** A fresh presigned bundle URL, valid for an hour. 409 when the bundle has
   *  the token inlined and that token is revoked or rotated away. */
  downloadUrl: (projectId: string, deploymentId: string) =>
    apiClient
      .get<FitLauncherDownloadUrlResponse>(
        API_ENDPOINTS.FIT_LAUNCHER.DOWNLOAD(projectId, deploymentId),
      )
      .then((r) => r.data),

  /** The bundle's README.md: the run instructions, as the ZIP has them. */
  readme: (projectId: string, deploymentId: string) =>
    apiClient
      .get<FitLauncherReadmeResponse>(API_ENDPOINTS.FIT_LAUNCHER.README(projectId, deploymentId))
      .then((r) => r.data),

  remove: (projectId: string, deploymentId: string) =>
    apiClient
      .delete<FitLauncherDeleteResponse>(API_ENDPOINTS.FIT_LAUNCHER.DETAIL(projectId, deploymentId))
      .then((r) => r.data),

  /** The new token is in this response only. */
  rotateToken: (projectId: string, deploymentId: string) =>
    apiClient
      .post<RotateApiKeyResponse>(API_ENDPOINTS.FIT_LAUNCHER.ROTATE_TOKEN(projectId, deploymentId))
      .then((r) => r.data),

  revokeToken: (projectId: string, deploymentId: string) =>
    apiClient
      .post<RevokeApiKeyResponse>(API_ENDPOINTS.FIT_LAUNCHER.REVOKE_TOKEN(projectId, deploymentId))
      .then((r) => r.data),
};
